export type HapticPreset =
  | "selection"
  | "light"
  | "medium"
  | "heavy"
  | "success"
  | "warning"
  | "error";

export type HapticInput = HapticPreset | number;

const isNativeCapacitorRuntime = () => {
  if (typeof window === "undefined") return false;
  const capacitor = (
    window as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor;
  return Boolean(capacitor?.isNativePlatform?.());
};

const presetToDuration = (preset: HapticPreset) => {
  switch (preset) {
    case "selection":
      return 12;
    case "light":
      return 16;
    case "medium":
      return 26;
    case "heavy":
      return 36;
    case "success":
      return 32;
    case "warning":
      return 28;
    case "error":
      return 40;
    default:
      return 24;
  }
};

const triggerNativeHaptic = async (input: HapticInput) => {
  if (!isNativeCapacitorRuntime()) return false;

  try {
    const { Haptics, ImpactStyle, NotificationType } = await import(
      "@capacitor/haptics"
    );

    if (typeof input === "number") {
      await Haptics.impact({
        style:
          input >= 32
            ? ImpactStyle.Heavy
            : input >= 22
              ? ImpactStyle.Medium
              : ImpactStyle.Light,
      });
      return true;
    }

    if (input === "selection") {
      await Haptics.selectionStart();
      await Haptics.selectionChanged();
      await Haptics.selectionEnd();
      return true;
    }

    if (input === "success" || input === "warning" || input === "error") {
      await Haptics.notification({
        type:
          input === "success"
            ? NotificationType.Success
            : input === "warning"
              ? NotificationType.Warning
              : NotificationType.Error,
      });
      return true;
    }

    await Haptics.impact({
      style:
        input === "heavy"
          ? ImpactStyle.Heavy
          : input === "medium"
            ? ImpactStyle.Medium
            : ImpactStyle.Light,
    });

    return true;
  } catch {
    return false;
  }
};

const triggerVibration = (input: HapticInput) => {
  if (typeof navigator === "undefined" || !navigator.vibrate) {
    return;
  }

  if (typeof input === "number") {
    navigator.vibrate(input);
    return;
  }

  navigator.vibrate(presetToDuration(input));
};

export function haptic(input: HapticInput = "light"): void {
  void (async () => {
    const didUseNative = await triggerNativeHaptic(input);
    if (!didUseNative) {
      triggerVibration(input);
    }
  })();
}
